/**
 * Texts Statistics Tab
 * Uses shared CampaignStatsDashboard with texts-specific data.
 * The group's tracker metrics aggregated by SMS text (custom-variable-6).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo, useState } from 'react';

import { useAuth, useCountryCreativeForStats, useTextPerformanceTags, useTextsWithStats } from '@/hooks';
import { Button } from '@/components/ui';
import { useStatsTrackerName } from '@/features/trackers/stats-tracker-context';

import {
  CampaignStatsDashboard,
  TruncatedTextCell,
  FILTER_FIELDS_TEXTS,
  GROUP_BY_OPTIONS_TEXTS,
  toRangeISO,
  mergeTextRowsById,
  toGenericStatsRowFromText,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import { cn } from '@/lib/utils';
import { applyDevTextGroupMock } from './devTextGroupMock';

interface TextsStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
}

export const TextsStatsTab: FC<TextsStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const trackerName = useStatsTrackerName();
  const state = useStatsTabState(workDate, 'texts');
  const [devMockTextGroups, setDevMockTextGroups] = useState(false);
  const creative = useCountryCreativeForStats(countryKey);
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );
  const perfTagsQuery = useTextPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByTextId = perfTagsQuery.data?.tagsByTextId ?? {};
  const tagDetailsByTextId = perfTagsQuery.data?.tagDetailsByTextId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;
  const templateIdsInDb = useMemo(
    () => new Set((creative.data?.smsTextsWithIds ?? []).map((t) => t.templateId)),
    [creative.data?.smsTextsWithIds],
  );
  const templateIdToText = useMemo(
    () =>
      new Map(
        (creative.data?.smsTextsWithIds ?? []).map((t) => [t.templateId, t.text]),
      ),
    [creative.data?.smsTextsWithIds],
  );

  const apiGroupBy = state.groupBy === 'gateway' ? 'gateway' : 'drop';
  const query = useTextsWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
    groupBy: apiGroupBy,
  });

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useTextsWithStats({
    countryKey,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
    groupBy: apiGroupBy,
    enabled: compare.compareMode,
  });
  const compareById = useMemo(() => {
    const generic = (compareQuery.data?.rows ?? []).map((r) => toGenericStatsRowFromText(r));
    const finalRows = state.groupBy === 'text' ? mergeTextRowsById(generic) : generic;
    const map = new Map<string, Record<string, number> | null>();
    for (const r of finalRows) map.set(r.id, r.voluum);
    return map;
  }, [compareQuery.data?.rows, state.groupBy]);

  const allTextRows = query.data?.rows ?? [];
  const displayTextRows = useMemo(
    () =>
      import.meta.env.DEV && devMockTextGroups
        ? applyDevTextGroupMock(allTextRows)
        : allTextRows,
    [allTextRows, devMockTextGroups],
  );
  const allGenericRows = useMemo(
    () =>
      displayTextRows.map((r) => {
        const base = toGenericStatsRowFromText(r);
        const textId = r.textId ? String(r.textId).trim() : '';
        const tags =
          textId
            ? (tagsByTextId[textId] ??
              Object.entries(tagsByTextId).find(
                ([k]) => k.toLowerCase() === textId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [displayTextRows, tagsByTextId],
  );

  const rowsForFiltering = useMemo(() => {
    if (state.groupBy === 'text') {
      const merged = mergeTextRowsById(allGenericRows);
      return merged.map((r) => {
        const textId = r.id;
        const tags =
          tagsByTextId[textId] ??
          Object.entries(tagsByTextId).find(
            ([k]) => k.toLowerCase() === textId.toLowerCase(),
          )?.[1] ??
          [];
        return { ...r, performanceTags: tags };
      });
    }
    return allGenericRows;
  }, [allGenericRows, state.groupBy, tagsByTextId]);

  const effectiveGroupBy =
    state.groupBy === 'text' ? 'none' : state.groupBy;

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    rowsForFiltering,
    state.filtersState,
    state.sort,
    workDate,
    effectiveGroupBy,
  );

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByTextId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByTextId]);

  const totals = query.data?.totals ?? {};
  const totalLabel = useMemo(() => {
    const dropCount = query.data?.dropCount ?? 0;
    const gatewayCount = query.data?.gatewayCount ?? 0;
    const textCount = query.data?.textCount ?? allTextRows.length;
    if (dropCount === 0 && gatewayCount === 0 && textCount === 0) return undefined;
    if (state.groupBy === 'gateway' && gatewayCount > 0) {
      return `Total (${gatewayCount.toLocaleString()} gateway${gatewayCount !== 1 ? 's' : ''}) (${textCount.toLocaleString()} text${textCount !== 1 ? 's' : ''})`;
    }
    return `Total (${dropCount.toLocaleString()} drop${dropCount !== 1 ? 's' : ''}) (${textCount.toLocaleString()} text${textCount !== 1 ? 's' : ''})`;
  }, [
    query.data?.dropCount,
    query.data?.gatewayCount,
    query.data?.textCount,
    allTextRows.length,
    state.groupBy,
  ]);

  const devMockToggle = import.meta.env.DEV ? (
    <Button
      type="button"
      variant={devMockTextGroups ? 'default' : 'outline'}
      size="sm"
      className={cn('h-8 text-xs', devMockTextGroups && 'shadow-sm')}
      onClick={() => setDevMockTextGroups((value) => !value)}
      title="Dev only: fills missing Text group/Text prompt metadata on real stats rows."
    >
      {devMockTextGroups ? 'Mock text groups on' : 'Mock text groups'}
    </Button>
  ) : null;

  const tagDetailsLookup = useCallback(
    (textId: string) => {
      if (!textId) return undefined;
      const direct = tagDetailsByTextId[textId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByTextId).find(
        ([k]) => k.toLowerCase() === textId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByTextId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const textId = row._originalText?.textId ?? row.id.split('::')[1] ?? row.id;
      const fullText = templateIdToText.get(textId) ?? row.label ?? undefined;
      const displayLabel = fullText ?? row.label ?? '—';
      const performanceTags = textId
        ? (tagsByTextId[textId] ??
          Object.entries(tagsByTextId).find(
            ([k]) => k.toLowerCase() === textId.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = textId ? tagDetailsLookup(textId) : undefined;
      return (
        <TruncatedTextCell
          label={displayLabel}
          textId={textId}
          fullText={fullText}
          inDb={templateIdsInDb.has(textId)}
          dropCount={row.dropCount}
          showDropCount={state.groupBy === 'text'}
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
        />
      );
    },
    [templateIdsInDb, templateIdToText, state.groupBy, tagsByTextId, tagDetailsLookup, tagColors],
  );

  return (
    <CampaignStatsDashboard
      tab="texts"
      title="Texts"
      emptyMessage="No texts found for this range."
      loadingTitle="Loading text performance data"
      loadingDesc="Fetching tracker metrics by drop and SMS text."
      errorPrefix="Failed to load text stats."
      firstColumnLabel="Text"
      firstColumnSortKey="text"
      rowUnitLabel="text"
      filterFields={FILTER_FIELDS_TEXTS}
      groupByOptions={GROUP_BY_OPTIONS_TEXTS}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={query.data?.total ?? allGenericRows.length}
      totalLabel={totalLabel}
      totalLabelDropsTooltip={`The ${trackerName} report returns rows by (drop, text). Limit 1000 caps rows, not drops. The top 1000 rows may span fewer drops; drops whose texts rank lower never appear. The full drop count requires a separate ${trackerName} report grouped by drop only.`}
      allRowsLength={allGenericRows.length}
      currency={query.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={query.isError ?? false}
      error={query.error}
      isLoading={query.isLoading ?? false}
      isFetching={query.isFetching ?? false}
      dataUpdatedAt={query.dataUpdatedAt}
      refetch={() => query.refetch()}
      showLimitSelect
      showGroupBySelect
      filterToolbarExtra={devMockToggle}
      tagFilterOptions={availableTags}
      csvExportUserId={user?.id}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
      compareCapable
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
    />
  );
};

export default TextsStatsTab;
